import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import Navigation from "../components/Navigation";
import AssessmentStatusBadge from "../utils/assessmentStatusLabel";
import Modal from '../components/Modal';
import Card from '../components/Card';
import Button from '../components/Buttons';
import Input from '../components/Input';
import { useApplicationTypes } from "../contexts/ApplicationTypeContext";
import { useUserBases } from "../contexts/UserBaseContext";
import { useDeploymentModels } from "../contexts/DeploymentModelContext";
import LoadingSpinner from '../components/LoadingSpinner';
import { Plus, FileText, Shield, Trash2, RefreshCw, Lock, Users, Eye } from 'lucide-react';
import Pagination from "../components/Pagination";
import { usePageSize } from "../hooks/usePageSize";
import { useRegions } from "../contexts/RegionContext";
import { useVerticals } from "../contexts/VerticalsContext";
import { useOrganizations } from "../contexts/OrganizationContext";
import DealAssignAssessModal from "../components/DealAssignAssessModal";
import AssessmentRespondentsModal from "../components/AssessmentRespondentsModal";
import AssessmentUnassignedModal from "../components/AssessmentUnassignedModal";
import DarkWebAssessment from "../components/DarkWebAssessment";
import DarkWebNewDealModal from "../components/DarkWebNewDealModal";
import DeleteAssessmentModal from './DeleteAssessmentModal';
import ReassessModal from './ReassessModal';
import { useRBAC } from "../hooks/useRBAC";
import { useURLState } from "../hooks/useURLState";
import AssessmentActivityLog from "../components/AssessmentActivityLog";
import { severityPostureScore } from '../utils/riskScoringEngine';

const DarkWebDeals = () => {
  const [deals, setDeals] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showNewDealModal, setShowNewDealModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPageParam, setCurrentPageParam] = useURLState('page');
  const currentPage = parseInt(currentPageParam) || 1;
  const [totalCount, setTotalCount] = useState(0);
  const [paging, setPaging] = useState(false);
  const [pageSize, setPageSize] = usePageSize('darkWebDeals');
  const listTopRef = useRef(null);
  const applicationTypes = useApplicationTypes() || [];
  const userBases = useUserBases() || [];
  const deploymentModels = useDeploymentModels() || [];
  const [showDarkWebNewDealModal, setShowDarkWebNewDealModal] = useState(false);

  const handleDealSelected = (deal) => {
    setShowDarkWebNewDealModal(false);
    setCurrentPageParam(1);
    fetchDeals();
  };

  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useApi();
  const { user } = useAuth();
  const { canAssess, getAssessDisabledReason, canManageOrganizationRisks, canManageDeal, canViewDeal, canAssessViaAssignment, canCreateDealOrBusinessUnit, authVersion, canDeleteAssessments } = useRBAC();
  const canDeleteAssessment = canDeleteAssessments(); // super admin only
  const selectedOrg = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const canManageRisks = canManageOrganizationRisks();
  const canManageRisksForDeal = (dealID) => canManageRisks || canManageDeal(dealID);
  const canAddDealOrBU = canCreateDealOrBusinessUnit(selectedOrg?.organizationId);

  const regions = useRegions();
  const [formData, setFormData] = useState({ regionId: '' });

  const { verticals } = useVerticals();
  const organizations = useOrganizations();

  // For assign modal: store the selected entity (deal)
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedEntityForAssign, setSelectedEntityForAssign] = useState(null);
  const [respondentsAssessmentId, setRespondentsAssessmentId] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [showReassessModal, setShowReassessModal] = useState(false);
  const [assessmentToReassess, setAssessmentToReassess] = useState(null);
  const [reassessing, setReassessing] = useState(false);

  const [showUnassignedAlert, setShowUnassignedAlert] = useState(false);

  // NOTE: fetchDeals name used consistently
  useEffect(() => {
    fetchDeals();
  }, [currentPage, pageSize, authVersion]);

  // Auto-select from navigation state (if any)
  useEffect(() => {
    const autoSelectId = location.state?.selecteddealID;
    if (autoSelectId && deals.length > 0) {
      const target = deals.find(a => a.dealID === autoSelectId);
      if (target) {
        setSelectedDeal(target);
      }
    }
  }, [deals, location.state]);


  // Fetch DarkWeb deals with risk summary from optimized endpoint
  const fetchDeals = async () => {
    setPaging(true);
    try {
      const org = JSON.parse(localStorage.getItem("selectedOrganization"));
      if (!org?.organizationId) {
        setLoading(false);
        return;
      }

      const data = await apiCall("GET", `/deals/summary?category=DarkWeb&page=${currentPage}&pageSize=${pageSize}`);

      const dealsWithRiskData = (data.items || []).map((deal) => ({
        dealID: deal.dealId,
        isPrivate: deal.isPrivate,
        dealName: deal.dealName,
        verticalId: deal.verticalId,
        domain: deal.domain,
        targetEndDate: deal.targetEndDate,
        hasAssessment: deal.applicationCount > 0,
        riskCounts: {
          CRITICAL: deal.riskCounts?.critical || 0,
          HIGH: deal.riskCounts?.high || 0,
          MEDIUM: deal.riskCounts?.medium || 0,
          LOW: deal.riskCounts?.low || 0,
        },
        totalRisks: (deal.riskCounts?.critical || 0) + (deal.riskCounts?.high || 0) +
                    (deal.riskCounts?.medium || 0) + (deal.riskCounts?.low || 0),
        activeRisks: deal.activeRisks,
        riskScore: calculateRiskScore({
          CRITICAL: deal.riskCounts?.critical || 0,
          HIGH: deal.riskCounts?.high || 0,
          MEDIUM: deal.riskCounts?.medium || 0,
          LOW: deal.riskCounts?.low || 0,
        }),
        riskLevel: deal.riskLevel === "NONE" ? "No Risk" : deal.riskLevel,
        latestAssessment: deal.assessmentId ? {
          darkWebAssessmentID: deal.assessmentId,
          darkWebAssignedToUserId: deal.assignedToUserId,
          darkWebAssignedToUserName: deal.assignedToUserName,
          darkWebStatus: deal.status,
          assigneeUserIds: deal.assigneeUserIds,
        } : null,
      }));

      // Backend /deals/summary already applies RBAC deal-scoping and returns a scoped
      // totalCount, so trust the server response directly. Re-filtering client-side broke
      // pagination for deal-scoped users by clobbering totalCount with the current page's length.
      setDeals(dealsWithRiskData);
      setTotalCount(data.totalCount || 0);
    } catch (error) {} finally {
      setLoading(false);
      setPaging(false);
    }
  };

  const calculateRiskScore = (riskCounts) => {
    return severityPostureScore(riskCounts);
  };

  const handleDeleteAssessment = async () => {
    if (!assessmentToDelete) return;
    setDeleting(true);
    try {
      await apiCall('DELETE', `/darkWebAssessments/${assessmentToDelete}`);
      setShowDeleteModal(false);
      setAssessmentToDelete(null);
      await fetchDeals();
    } catch (error) {} finally {
      setDeleting(false);
    }
  };

  const handleReassessAssessment = async () => {
    if (!assessmentToReassess) return;
    setReassessing(true);
    try {
      await apiCall('POST', `/darkWebAssessments/${assessmentToReassess}/reassess`);
      setShowReassessModal(false);
      setAssessmentToReassess(null);
      navigate(`/DarkWebAssessment/${assessmentToReassess}?step=review`);
    } catch (error) {} finally {
      setReassessing(false);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />

      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-content-primary"> <span className="text-blue-800">Dark Web Deals Management</span></h1>
          <p className="mt-2 text-gray-600">Manage Deals for Dark Web risk assessment</p>
        </div>

        {/* Full width deals card */}
        <Card>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Deals</h2>

            <div className="flex items-center gap-2">
                {canAddDealOrBU && (
                  <Button size="sm" onClick={() => setShowDarkWebNewDealModal(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                     New Deal
                  </Button>
                )}

              {/* ✅ Assign Deal Modal (contains deal list + create new) */}
              {showDarkWebNewDealModal && (
                <DarkWebNewDealModal
                  isOpen={showDarkWebNewDealModal}
                  onClose={() => setShowDarkWebNewDealModal(false)}
                  onDealSelected={handleDealSelected}
                  existingDealIds={deals.map(d => d.dealID)}
                />
              )}

              {/* Quick Assessment: (top-level button) - hidden */}
              {false && (
              <Button
                size="sm"
                onClick={() => {


                  navigate("/DarkWebAssessment", {
                    state: {
                      dealID: deals.dealID,
                      dealName: deals.dealName,
                      dealDescription: deals.description,
                      isNewDeal: true,
                    },
                  });
                }}
              >
                Quick Assessment
              </Button>
              )}
            </div>
          </div>

          <div className="space-y-3" ref={listTopRef}>
            {deals.map((deal) => (
              <div
                key={deal.dealID}
                className="p-4 border-2 rounded-lg border-gray-200"
              >
                <div className="flex gap-4 items-start">
                  <div className="flex-1 min-w-0">
                    {/* top row */}
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-content-primary">{deal.dealName}{deal.isPrivate && <span className="inline-flex items-center gap-1 ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded align-middle"><Lock className="h-3 w-3" />Private</span>}<AssessmentStatusBadge status={deal.latestAssessment?.darkWebStatus} /></h3>
                        <p className="text-sm text-gray-600">{verticals.find(v => v.verticalId === deal.verticalId)?.verticalName || "N/A"}</p>
                        {deal.description && <p className="text-sm text-gray-500 mt-1">{deal.description}</p>}
                        {deal.TargetEndDate && <p className="text-xs text-gray-500 mt-1">Due: {new Date(deal.TargetEndDate).toLocaleDateString()}</p>}
                        {deal.latestAssessment?.darkWebAssignedToUserName && (
                          <p className="text-xs text-blue-600 mt-1">
                            Assigned to: <span className="font-semibold">{deal.latestAssessment.darkWebAssignedToUserName}</span>
                          </p>
                        )}
                      </div>
                    </div>
                    {/* Risk details */}
                    {deal.hasAssessment ? (
                      <div className="mt-2">
                        <div className="flex flex-wrap gap-2">
                          {deal.riskCounts?.CRITICAL > 0 && <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">{deal.riskCounts.CRITICAL} Critical</span>}
                          {deal.riskCounts?.HIGH > 0 && <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">{deal.riskCounts.HIGH} High</span>}
                          {deal.riskCounts?.MEDIUM > 0 && <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">{deal.riskCounts.MEDIUM} Medium</span>}
                          {deal.riskCounts?.LOW > 0 && <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">{deal.riskCounts.LOW} Low</span>}
                        </div>
                        <div className="mt-2">
                          <div className="text-xs text-gray-500 mb-1">Risk Score: {deal.riskScore || 0}%</div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div className={`h-3 rounded-full ${deal.riskScore <= 40 ? 'bg-green-500' : deal.riskScore <= 60 ? 'bg-yellow-500' : deal.riskScore <= 80 ? 'bg-orange-500' : 'bg-red-500'}`} style={{ width: `${deal.riskScore || 0}%` }} />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-gray-500">No Assessment Data</div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-3">
                  {canManageRisksForDeal(deal.dealID) && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={deal.hasAssessment && deal.latestAssessment?.darkWebStatus === "completed"}
                      title={deal.hasAssessment && deal.latestAssessment?.darkWebStatus === "completed" ? "Assessment is completed" : ""}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedEntityForAssign(deal);
                        setShowAssignModal(true);
                      }}
                    >
                      <Shield className="h-3 w-3 mr-1" />
                      {deal.latestAssessment?.darkWebAssignedToUserId ? "Reassign" : "Assign"}
                    </Button>
                  )}

                  {canManageRisksForDeal(deal.dealID) && deal.latestAssessment?.darkWebAssessmentID && (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="whitespace-nowrap"
                      title="Manage additional respondents for this assessment"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRespondentsAssessmentId(deal.latestAssessment.darkWebAssessmentID);
                      }}
                    >
                      <Users className="h-4 w-4 mr-1" />
                      Respondents
                    </Button>
                  )}

                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={
                      !canAssess(deal.latestAssessment?.darkWebAssignedToUserId, null, 'DEAL', deal.dealID, deal.latestAssessment?.assigneeUserIds || []) ||
                      (deal.hasAssessment && (deal.latestAssessment?.darkWebStatus === "completed" || deal.latestAssessment?.darkWebStatus === "analyzing"))
                    }
                    title={
                      (deal.latestAssessment?.darkWebStatus === "completed" ? "Assessment is completed"
                        : deal.latestAssessment?.darkWebStatus === "analyzing" ? "Assessment is being analyzed"
                        : "")
                        || getAssessDisabledReason(deal.latestAssessment?.darkWebAssignedToUserId, null, 'DEAL', deal.dealID, deal.latestAssessment?.assigneeUserIds || [])
                        || ""
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!deal.latestAssessment?.darkWebAssignedToUserId) {
                        setShowUnassignedAlert(true);
                        return;
                      }
                      const darkWebPath = deal.latestAssessment?.darkWebAssessmentID
                        ? `/DarkWebAssessment/${deal.latestAssessment.darkWebAssessmentID}`
                        : '/DarkWebAssessment';
                      navigate(darkWebPath, {
                        state: {
                          dealID: deal.dealID,
                          dealName: deal.dealName,
                          verticalId: deal.verticalId,
                          regionId: deal.regionId,
                        }
                      });
                    }}
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    Assess
                  </Button>

                  {/* Viewing mirrors the backend's GetViewableAssessmentAsync: deal scope or assignment. */}
                  {deal.hasAssessment && deal.latestAssessment?.darkWebAssessmentID && deal.latestAssessment?.darkWebStatus === "completed" &&
                    (canViewDeal(deal.dealID) || canAssessViaAssignment(deal.latestAssessment?.darkWebAssignedToUserId, deal.latestAssessment?.assigneeUserIds || [])) && (
                    <Button
                      size="sm"
                      variant="secondary"
                      title="View the completed assessment's questions, answers, notes, and attachments (read-only)"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/DarkWebAssessment/${deal.latestAssessment.darkWebAssessmentID}?step=review`);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View Answers
                    </Button>
                  )}

                  {deal.hasAssessment && deal.latestAssessment?.darkWebAssessmentID && deal.latestAssessment?.darkWebStatus === "completed" ? (
                    <Button
                      size="sm"
                      variant="primary"
                      className="px-1 py-1 h-9 text-xs"
                      disabled={deal.totalRisks === 0}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/DarkWebAssessment/${deal.latestAssessment.darkWebAssessmentID}/risks`);
                      }}
                    >
                      <Shield className="h-4 w-4 mr-1" />
                      View Risks ({deal.activeRisks ?? deal.totalRisks})
                    </Button>
                  ) : deal.totalRisks > 0 ? (
                    // No assessment, but tagged risks (e.g. Enzoic) are scoped to this deal — open the
                    // tag-aware All Risks list filtered to DarkWeb + this deal.
                    <Button
                      size="sm"
                      variant="primary"
                      className="px-1 py-1 h-9 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/risks?types=DarkWeb&deal=${deal.dealID}`);
                      }}
                      title="View tagged risks for this deal"
                    >
                      <Shield className="h-4 w-4 mr-1" />
                      View Risks ({deal.activeRisks ?? deal.totalRisks})
                    </Button>
                  ) : (
                    <Button size="sm" variant="primary" className="px-1 py-1 h-9 text-xs" disabled title="No risks">
                      <Shield className="h-4 w-4 mr-1" />
                      View Risks (0)
                    </Button>
                  )}

                  {canManageOrganizationRisks() && deal.latestAssessment?.darkWebAssessmentID && (
                    <Button size="sm" variant="warning" className="px-1 py-1 h-9 text-xs"
                      disabled={deal.latestAssessment?.darkWebStatus !== 'completed'}
                      onClick={(e) => {
                        e.stopPropagation();
                        setAssessmentToReassess(deal.latestAssessment.darkWebAssessmentID);
                        setShowReassessModal(true);
                      }}>
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Reassess
                    </Button>
                  )}

                  {canDeleteAssessment && deal.latestAssessment?.darkWebAssessmentID && (
                    <Button
                      size="sm"
                      variant="danger"
                      className="px-1 py-1 h-9 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAssessmentToDelete(deal.latestAssessment.darkWebAssessmentID);
                        setShowDeleteModal(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  )}
                    </div>
                  </div>
                  {deal.latestAssessment?.darkWebAssessmentID && (
                    <>
                      <div className="w-px bg-gray-200 self-stretch flex-shrink-0 mx-1"></div>
                      <div className="w-80 flex-shrink-0 bg-gray-50 rounded-xl border border-gray-200 px-4 py-3 shadow-sm">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Activity</p>
                        <AssessmentActivityLog assessmentType="darkweb" assessmentId={String(deal.latestAssessment.darkWebAssessmentID)} hideQuestionEvents />
                      </div>
                    </>
                  )}
                </div>

              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={setCurrentPageParam}
            onPageSizeChange={setPageSize}
            scrollTargetRef={listTopRef}
            busy={paging}
          />
        </Card>

        {showAssignModal && selectedEntityForAssign && (
          <DealAssignAssessModal
            isOpen={showAssignModal}
            onClose={() => setShowAssignModal(false)}
            dealID={selectedEntityForAssign.dealID}
            isPrivate={selectedEntityForAssign.isPrivate}
            assessmentType="darkweb"
            organizationId={JSON.parse(localStorage.getItem("selectedOrganization"))?.organizationId}
            hasExistingAssessment={selectedEntityForAssign.hasAssessment}
            refreshData={fetchDeals}
          />
        )}

        <AssessmentRespondentsModal
          isOpen={respondentsAssessmentId != null}
          onClose={() => setRespondentsAssessmentId(null)}
          assessmentType="darkweb"
          assessmentId={respondentsAssessmentId}
          refreshData={fetchDeals}
        />

      </div>

      <DeleteAssessmentModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setAssessmentToDelete(null); }}
        onConfirm={handleDeleteAssessment}
        isDeleting={deleting}
      />

      <ReassessModal
        isOpen={showReassessModal}
        onClose={() => { setShowReassessModal(false); setAssessmentToReassess(null); }}
        onConfirm={handleReassessAssessment}
        isReassessing={reassessing}
      />

      <AssessmentUnassignedModal
        isOpen={showUnassignedAlert}
        onClose={() => setShowUnassignedAlert(false)}
      />
    </div>
  );
};

export default DarkWebDeals;
